import React from 'react';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Select from '../Select/Select';

interface PaginationProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  loading?: boolean;
  itemLabel?: string; // e.g., "reports", "responses", "items"
  className?: string;
  showPageSizeOptions?: boolean;
  onlyShowIfRequired?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  loading = false,
  itemLabel = 'items',
  className = '',
  showPageSizeOptions = false,
  onlyShowIfRequired = true,
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);

  if (onlyShowIfRequired && totalPages < 2) return null;

  const pageSizeOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ];

  return (
    <div className={`flex flex-col sm:flex-row justify-between items-center mt-6 gap-4 mb-10 ${className}`}>
      {/* Page Size Selector */}
      {showPageSizeOptions && (
        <div className="flex items-center gap-2">
          <Select
            value={pageSize}
            options={pageSizeOptions}
            handleSelect={option => {
              onPageSizeChange(Number(option.value));
            }}
            disabled={loading}
            className="w-20"
          />
          <span className="text-sm text-gray-700 ml-5">per page</span>
        </div>
      )}

      {/* Page Navigation */}
      <div className="flex items-center gap-2">
        <div className="text-sm text-gray-700">
          {totalItems > 0 ? (
            <>
              {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalItems)} of {totalItems}{' '}
              {itemLabel}
            </>
          ) : (
            `No ${itemLabel} found`
          )}
        </div>
        <button
          onClick={() => onPageChange(0)}
          disabled={currentPage === 0 || loading}
          className="px-2 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          <ChevronDoubleLeftIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0 || loading}
          className="px-2 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>

        {/* Page Numbers */}
        <div className="flex gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i;
            } else if (currentPage < 3) {
              pageNum = i;
            } else if (currentPage >= totalPages - 3) {
              pageNum = totalPages - 5 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                disabled={loading}
                className={`px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                  currentPage === pageNum ? 'bg-zinc-900 text-white' : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {pageNum + 1}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1 || loading}
          className="px-2 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => onPageChange(totalPages - 1)}
          disabled={currentPage >= totalPages - 1 || loading}
          className="px-2 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          <ChevronDoubleRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
